/* eslint-disable camelcase */
import React, { useState, useContext } from "react"
import { styled, createTheme, ThemeProvider } from "@mui/material/styles"
import { Box, Typography, Paper, Grid, TextField, MenuItem, Button, Chip } from "@mui/material"
import { Search, Gavel } from "@mui/icons-material"
import CheckIcon from "@mui/icons-material/Check"
import { useNavigate } from "react-router-dom"
import dayjs from "dayjs"

import Header from "../../layouts/Header"
import BackIcon from "../../components/BackIcon"
import CustomConditionalActionsTableServer from "../../components/CustomConditionalActionsTableServerSide"
import { GlobalContext } from "../../contexts/GlobalContext"
import getIconComponent from "../utils/getIconComponent"
import normalFormatDate from "../utils/date/DDMMYYYFormatDate"
import {
  handleExportDataPdfLGScreen,
  handleExportDataPdfSMScreen,
  handleAllExportDataCSV,
} from "../utils/reactTableActions/exportToolbarActions"

const StyledRootStyles = styled(Box)(({ theme }) => ({
  display: "flex",
  flexDirection: "column",
  marginTop: theme.spacing(2),
  flexGrow: 1,
  padding: "0 16px",
  height: "100vh",
}))

const theme = createTheme({
  palette: { primary: { main: "#196C87" }, secondary: { main: "#196C87" } },
})

const TrasladoDptoLegalIndex = () => {
  const navigate = useNavigate()
  const { selectedMenuInfo } = useContext(GlobalContext)

  const [filtroLegal, setFiltroLegal] = useState(0)
  const [filtroMoto, setFiltroMoto] = useState("TODAS")
  const [fechaDesde, setFechaDesde] = useState(dayjs().format("YYYY-MM-DD"))
  const [fechaHasta, setFechaHasta] = useState(dayjs().format("YYYY-MM-DD"))
  const [facturaNum, setFacturaNum] = useState("")

  const buildQueryString = () => {
    return `?filtroLegal=${filtroLegal}&filtroMoto=${filtroMoto}&fechaDesde=${fechaDesde}&fechaHasta=${fechaHasta}&facnumfac=${facturaNum}`
  }

  const [queryString, setQueryString] = useState(buildQueryString())

  const handleBuscar = () => {
    setQueryString(buildQueryString())
  }

  return (
    <ThemeProvider theme={theme}>
      <Header />
      <div className="main main-app p-3 p-lg-4">
        <BackIcon />

        <Box
          sx={{
            textAlign: "center",
            mb: 2,
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            color: "#196C87",
          }}
        >
          <Gavel sx={{ fontSize: 32, mr: 1 }} />
          <Typography variant="h5" fontWeight="bold" color="primary">
            Deuda en Departamento Legal
          </Typography>
        </Box>

        <StyledRootStyles>
          <Paper elevation={2} sx={{ p: 3, mb: 3, borderRadius: 2, borderTop: "4px solid #196C87" }}>
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Parámetros de Búsqueda
            </Typography>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Estado Legal"
                  value={filtroLegal}
                  onChange={(e) => setFiltroLegal(e.target.value)}
                  size="small"
                >
                  <MenuItem value={0}>Pendiente / No Legal</MenuItem>
                  <MenuItem value={1}>En PreLegal</MenuItem>
                  <MenuItem value={2}>En Legal</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  select
                  fullWidth
                  label="Estado Vehículo"
                  value={filtroMoto}
                  onChange={(e) => setFiltroMoto(e.target.value)}
                  size="small"
                >
                  <MenuItem value="TODAS">Todas</MenuItem>
                  <MenuItem value="INCAUTADA">Incautada</MenuItem>
                  <MenuItem value="NO_INCAUTADA">No Incautada</MenuItem>
                </TextField>
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Desde"
                  type="date"
                  value={fechaDesde}
                  onChange={(e) => setFechaDesde(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Fecha Hasta"
                  type="date"
                  value={fechaHasta}
                  onChange={(e) => setFechaHasta(e.target.value)}
                  size="small"
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6} md={2}>
                <TextField
                  fullWidth
                  label="Documento Inicial/Final"
                  placeholder="Nº Factura"
                  value={facturaNum}
                  onChange={(e) => setFacturaNum(e.target.value)}
                  size="small"
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <Button
                  fullWidth
                  variant="contained"
                  startIcon={<Search />}
                  onClick={handleBuscar}
                  sx={{ height: "40px" }}
                >
                  Ejecutar Búsqueda
                </Button>
              </Grid>
            </Grid>
          </Paper>

          <Paper elevation={2} sx={{ p: 1, borderRadius: 2 }}>
            <CustomConditionalActionsTableServer
              endpoint={`/TrasladoaDptoLegal/searchFacturasLegal${queryString}`}
              errorMsgFilterSearch="Error al cargar la bandeja de legal"
              queryKeyModal={`TrasladoLegal_Dinamico_${queryString}`}
              perPage={10}
              rowActionsWidthTable={120}
              rowActions={(row) => {
                const gestionarAction = selectedMenuInfo?.data?.barraAcciones?.find(
                  (a) => a.acccaption === "GESTIONAR" || a.acccaption === "EDITAR",
                )
                return [
                  {
                    label: gestionarAction ? gestionarAction.acccaption : "Gestionar Traslado",
                    key: "gestionar",
                    icon: gestionarAction ? (
                      getIconComponent(gestionarAction.accnameicono, gestionarAction.acctipoico)
                    ) : (
                      <CheckIcon color="success" />
                    ),
                    // ⭐ NAVEGACIÓN CORREGIDA POR STATE (ESTÁNDAR SIAC)
                    onClick: (row) => navigate("Editar", { state: { factura: row.original } }),
                  },
                ]
              }}
              topToolbarCustomActions={({ table, device }) => {
                const acciones = selectedMenuInfo?.data?.barraAcciones || []
                const exportarAction = acciones.find((a) => a.acccaption === "EXPORTAR")
                if (exportarAction) {
                  return [
                    {
                      type: "dropdown",
                      label: exportarAction.acccaption,
                      key: "exportDropdown",
                      icon: getIconComponent(exportarAction.accnameicono, exportarAction.acctipoico),
                      actions: [
                        {
                          label: "Exportar PDF",
                          key: "pdf",
                          onClick: ({ columns, data }) => {
                            const title = `Bandeja de Departamento Legal`
                            device === "sm"
                              ? handleExportDataPdfSMScreen(columns, data, title, title)
                              : handleExportDataPdfLGScreen(columns, table.getCoreRowModel().rows, title, title)
                          },
                        },
                        {
                          label: "Exportar CSV",
                          key: "csv",
                          onClick: ({ data }) => handleAllExportDataCSV(data, `Legal_SIAC`),
                        },
                      ],
                    },
                  ]
                }
                return []
              }}
              columnsTable={[
                {
                  accessorKey: "facnumfac",
                  header: "Factura",
                  size: 130,
                  Cell: ({ cell }) => <span style={{ fontWeight: "bold" }}>{cell.getValue()}</span>,
                },
                {
                  accessorKey: "facfecemi",
                  header: "Emisión",
                  size: 110,
                  Cell: ({ cell }) => normalFormatDate(cell.getValue()),
                },
                { accessorKey: "clinombre", header: "Nombre del Cliente", size: 280 },
                {
                  accessorKey: "facflaglegal",
                  header: "Estado Legal",
                  size: 140,
                  Cell: ({ cell }) => {
                    const val = cell.getValue()
                    if (val === 1)
                      return <Chip label="PRELEGAL" color="warning" size="small" sx={{ fontWeight: "bold" }} />
                    if (val === 2)
                      return <Chip label="EN LEGAL" color="error" size="small" sx={{ fontWeight: "bold" }} />
                    return <Chip label="NO LEGAL" color="success" size="small" variant="outlined" />
                  },
                },
                {
                  accessorKey: "tarnumero",
                  header: "Vehículo",
                  size: 140,
                  Cell: ({ cell }) => (
                    <span
                      style={{
                        fontStyle: !cell.getValue() ? "italic" : "normal",
                        color: !cell.getValue() ? "#9e9e9e" : "inherit",
                      }}
                    >
                      {cell.getValue() || "Sin Vehículo"}
                    </span>
                  ),
                },
                {
                  accessorKey: "facsaldo",
                  header: "Saldo Deuda ($)",
                  size: 150,
                  Cell: ({ cell }) => {
                    const val = Number(cell.getValue() || 0)
                    return (
                      <span style={{ fontWeight: "bold", color: val > 0 ? "#b71c1c" : "inherit", fontSize: "1.05em" }}>
                        ${val.toFixed(2)}
                      </span>
                    )
                  },
                },
              ]}
            />
          </Paper>
        </StyledRootStyles>
      </div>
    </ThemeProvider>
  )
}

export default TrasladoDptoLegalIndex
